import type { FormDialogRef } from '@/components'
import {
  Button,
  FAIcon,
  Field,
  FieldGroup,
  FieldLabel,
  FormDialog,
  NumberInput,
  Textarea,
} from '@/components'
import { cn } from '@/lib/utils'
import { useFormWatch } from '@/utils'
import { useMutation } from '@apollo/client'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useRef } from 'react'
import { isMobile } from 'react-device-detect'
import { Controller, useForm } from 'react-hook-form'
import z from 'zod'
import {
  CREATE_SALARY_CORRECTION_MUTATION,
  PATCH_SALARY_CORRECTION_MUTATION,
} from '../mutations'
import { DepartmentCombobox } from '@/components/combobox/department-combox'
import { FacilityCombobox } from '@/components/combobox/facility-combobox'
import { SalaryCodeCombobox } from '@/components/combobox/salary-code-combobox'
import type { SalaryCorrectionNode } from '../types.graphql'

function useSalaryCorrectionSchema() {
  const translations = useTranslate({
    userRequired: 'validation.field-required',
  })

  return z.object({
    id: z.string().optional(),
    user: z.string().min(1, translations.userRequired),
    department: z.string(),
    facility: z.string(),
    salaryCode: z.string(),
    reason: z.string(),
    amount: z.number(),
  })
}

export type SalaryCorrectionForm = z.infer<
  ReturnType<typeof useSalaryCorrectionSchema>
>

type SalaryCorrection = SalaryCorrectionForm & { id: string }

export interface SalaryCorrectionDialogRef {
  open(salaryCorrection: SalaryCorrection): void
}

function useInitialFilters(
  userId: string,
  correction?: SalaryCorrectionNode
): SalaryCorrectionForm {
  return {
    user: userId,
    department: correction?.department?.id || '',
    facility: correction?.facility?.id || '',
    salaryCode: correction?.salaryCode?.id || '',
    reason: correction?.reason || '',
    amount: correction?.amount || 0,
    id: correction?.id || '',
  }
}

interface Props {
  correction?: SalaryCorrectionNode
  userId: string
  year: number
  month: number

  hideTrigger?: boolean

  onSubmit?(): void
}

export function SalaryCorrectionDialog({
  correction,
  userId,
  year,
  month,
  hideTrigger = false,
  onSubmit,
}: Props) {
  const translations = useTranslate({
    labels: {
      user: 'common.user',
      coment: 'common.comment',
      department: 'common.department',
      facility: 'common.facility',
      value: 'common.value',
      salaryCode: 'common.salary-code',
    },
    headerAdd: 'salary.salary-correction-dialog.header-add',
    headerEdit: 'salary.salary-correction-dialog.header-edit',
    add: 'common.add',
  })

  const dialogRef = useRef<FormDialogRef>(null)

  const form = useForm<SalaryCorrectionForm>({
    resolver: zodResolver(useSalaryCorrectionSchema()),
    defaultValues: useInitialFilters(userId, correction),
  })

  const salaryCode = useFormWatch(form, 'salaryCode')

  const salaryCorrectionId = useFormWatch(form, 'id')

  const [createSalaryCorrection] = useMutation(
    CREATE_SALARY_CORRECTION_MUTATION,
    {
      refetchQueries: [],
      onCompleted() {
        dialogRef.current?.close()
      },
    }
  )

  const [patchSalaryCorrection] = useMutation(
    PATCH_SALARY_CORRECTION_MUTATION,
    {
      refetchQueries: [],
      onCompleted() {
        dialogRef.current?.close()
      },
    }
  )
  const confirmDisabled = !salaryCode

  function resetForm() {
    setTimeout(() => {
      form.reset()
    }, 300)
  }

  async function handleSubmit(values: SalaryCorrectionForm) {
    if (!values.id)
      await createSalaryCorrection({
        variables: {
          input: {
            user: values.user,
            reason: values.reason,
            department: values.department,
            facility: values.facility,
            salaryCode: values.salaryCode,
            amount: values.amount,
            year: year,
            month: month,
          },
        },
      })
    else
      await patchSalaryCorrection({
        variables: {
          id: values.id,
          input: {
            user: values.user,
            reason: values.reason,
            department: values.department,
            facility: values.facility,
            salaryCode: values.salaryCode,
            amount: values.amount,
            year: year,
            month: month,
          },
        },
      })
    onSubmit?.()
  }

  return (
    <FormDialog
      ref={dialogRef}
      form={form}
      trigger={
        <Button className={cn(hideTrigger && 'hidden')} size="sm">
          <FAIcon icon={correction ? 'edit' : 'plus'} />
          {!correction ? <span>{translations.add}</span> : null}
        </Button>
      }
      header={
        !salaryCorrectionId ? translations.headerAdd : translations.headerEdit
      }
      noCloseOnEscape
      confirmDisabled={confirmDisabled}
      error={undefined}
      onClose={resetForm}
      onSubmit={handleSubmit}
    >
      <FieldGroup>
        <Controller
          name="amount"
          control={form.control}
          render={({ field, fieldState }) => (
            <Field data-invalid={!!fieldState.error}>
              <FieldLabel>{translations.labels.value}</FieldLabel>
              <NumberInput {...field} decimalScale={2} />
            </Field>
          )}
        />
        <Controller
          name="salaryCode"
          control={form.control}
          render={({ field, fieldState }) => (
            <Field data-invalid={!!fieldState.error}>
              <FieldLabel>{translations.labels.salaryCode}</FieldLabel>
              <SalaryCodeCombobox
                {...field}
                mode="single"
                onChange={value => {
                  field.onChange(value)
                }}
              />
            </Field>
          )}
        />
        <Controller
          name="department"
          control={form.control}
          render={({ field, fieldState }) => (
            <Field data-invalid={!!fieldState.error}>
              <FieldLabel>{translations.labels.department}</FieldLabel>
              <DepartmentCombobox
                {...field}
                mode="single"
                clearable
                onChange={value => {
                  field.onChange(value)
                }}
              />
            </Field>
          )}
        />
        <Controller
          name="facility"
          control={form.control}
          render={({ field, fieldState }) => (
            <Field data-invalid={!!fieldState.error}>
              <FieldLabel>{translations.labels.facility}</FieldLabel>
              <FacilityCombobox
                {...field}
                mode="single"
                clearable
                onChange={value => {
                  field.onChange(value)
                }}
              />
            </Field>
          )}
        />
        <Controller
          name="reason"
          control={form.control}
          render={({ field, fieldState }) => (
            <Field data-invalid={!!fieldState.error}>
              <FieldLabel>{translations.labels.coment}</FieldLabel>

              <Textarea {...field} rows={isMobile ? 6 : 4} />
            </Field>
          )}
        />
      </FieldGroup>
    </FormDialog>
  )
}
